import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Trophy, TrendingUp, Users, Percent, Coins, Loader2, RefreshCw, ChevronDown, ListFilter, Info,
  Compass,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";

const STATUS_LABELS = {
  abierto: "Abierto", cotizaciones: "Cotizaciones", cerrado: "Cerrado",
  terminado: "Terminado", archivado: "Archivado", cancelado: "Cancelado", viaje_empresa: "Viaje de Empresa",
};
const SOLD_DEFAULT = ["abierto", "cerrado", "terminado", "viaje_empresa"];

const pct = (n) => `${(n || 0).toFixed(1)}%`;
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addMonths = (d, n) => { const x = new Date(d); x.setMonth(x.getMonth() + n); return x; };

const PERIODOS = [
  { key: "este_ano", label: "Este año" },
  { key: "mes_pasado", label: "Mes pasado" },
  { key: "3m", label: "Últimos 3 meses" },
  { key: "6m", label: "Últimos 6 meses" },
  { key: "1a", label: "Último año" },
  { key: "custom", label: "Personalizado" },
];

function presetRange(p) {
  const today = new Date();
  const y = today.getFullYear();
  if (p === "este_ano") return { desde: `${y}-01-01`, hasta: iso(today) };
  if (p === "mes_pasado") {
    const first = new Date(y, today.getMonth() - 1, 1);
    const last = new Date(y, today.getMonth(), 0);
    return { desde: iso(first), hasta: iso(last) };
  }
  if (p === "3m") return { desde: iso(addMonths(today, -3)), hasta: iso(today) };
  if (p === "6m") return { desde: iso(addMonths(today, -6)), hasta: iso(today) };
  if (p === "1a") return { desde: iso(addMonths(today, -12)), hasta: iso(today) };
  return null;
}

function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="flex items-center gap-3 p-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><Icon className="h-5 w-5" /></div>
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="truncate font-heading text-xl font-bold tracking-tight">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function SourcesPopover({ sources, setSources, disponibles }) {
  const toggle = (k) => setSources((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const label = sources.length === 0
    ? "Todas"
    : sources.length === 1
    ? sources[0]
    : `${sources.length} seleccionadas`;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 gap-2" data-testid="iv-sources-btn">
          <Compass className="h-4 w-4" /> Fuente · {label}
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-2">
        <div className="flex items-center justify-between px-2 py-1">
          <p className="text-xs font-medium text-muted-foreground">Origen del lead</p>
          {sources.length > 0 && (
            <button className="text-xs text-primary hover:underline" onClick={() => setSources([])}>
              Limpiar
            </button>
          )}
        </div>
        {(disponibles || []).map((k) => (
          <label
            key={k}
            className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm capitalize hover:bg-muted"
            data-testid={`iv-source-${k}`}
          >
            <Checkbox checked={sources.includes(k)} onCheckedChange={() => toggle(k)} />
            {k === "sin_fuente" ? "(sin fuente)" : k}
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function EstadosPopover({ estados, setEstados }) {
  const toggle = (k) => setEstados((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 gap-2" data-testid="iv-estados-btn">
          <ListFilter className="h-4 w-4" /> Estados · {estados.length}
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-2">
        <p className="px-2 py-1 text-xs font-medium text-muted-foreground">Estados del viaje</p>
        {Object.entries(STATUS_LABELS).map(([k, label]) => (
          <label key={k} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted" data-testid={`iv-status-${k}`}>
            <Checkbox checked={estados.includes(k)} onCheckedChange={() => toggle(k)} />
            {label}
          </label>
        ))}
        <div className="mt-1 flex justify-between border-t px-2 pt-2">
          <button className="text-xs text-primary hover:underline" onClick={() => setEstados(Object.keys(STATUS_LABELS))}>Todos</button>
          <button className="text-xs text-muted-foreground hover:underline" onClick={() => setEstados(SOLD_DEFAULT)}>Solo vendidos</button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

const COLS = [
  { key: "ventas", label: "Ventas", fmt: (v) => v ?? 0 },
  { key: "leads", label: "Leads", fmt: (v) => v ?? 0 },
  { key: "conversion_pct", label: "% Conversión", fmt: pct },
  { key: "margen_venta_pct", label: "Margen medio venta", fmt: pct },
  { key: "rentabilidad_pct", label: "Rentabilidad media", fmt: (v, r) => (
    <>
      {pct(v)}
      {(r?.viajes_rent ?? 0) > 0 && (
        <span className="ml-1 text-[11px] font-normal text-muted-foreground">({r.viajes_rent} viaje{r.viajes_rent === 1 ? "" : "s"})</span>
      )}
    </>
  ) },
];

const SOURCE_COLS = [
  { key: "ventas", label: "Ventas", fmt: (v) => v ?? 0 },
  { key: "volumen_venta_eur", label: "Volumen", fmt: (v) => `€${Math.round(v || 0).toLocaleString("es-ES")}` },
  { key: "margen_venta_pct", label: "Margen venta", fmt: pct },
  { key: "rentabilidad_pct", label: "Rentabilidad", fmt: (v, r) => (
    <>
      {pct(v)}
      {(r?.viajes_rent ?? 0) > 0 && (
        <span className="ml-1 text-[11px] font-normal text-muted-foreground">({r.viajes_rent})</span>
      )}
    </>
  ) },
];

export default function InformeVentasPage() {
  const [periodo, setPeriodo] = useState("este_ano");
  const [custom, setCustom] = useState(() => presetRange("3m"));
  const [estados, setEstados] = useState(SOLD_DEFAULT);
  const [sources, setSources] = useState([]);   // vacío = todas

  const range = periodo === "custom" ? custom : presetRange(periodo);
  const desde = range.desde, hasta = range.hasta;

  const q = useQuery({
    queryKey: ["bi-informe-ventas", desde, hasta, estados, sources],
    queryFn: async () => (await api.get("/bi/informe-ventas", {
      params: {
        desde, hasta,
        estados: estados.join(","),
        sources: sources.length ? sources.join(",") : undefined,
      },
    })).data,
    enabled: estados.length > 0 && !!desde && !!hasta,
  });
  const data = q.data;
  const rows = data?.rows || [];
  const total = data?.total || {};
  const bySource = data?.by_source || [];
  const sourcesDisponibles = data?.sources_disponibles || [];

  return (
    <div className="space-y-5" data-testid="informe-ventas-page">
      <div>
        <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight">
          <Trophy className="h-6 w-6 text-primary" /> Informe de ventas
        </h1>
        <p className="text-sm text-muted-foreground">Comparativa del equipo comercial: ventas, leads, conversión y rentabilidad por agente.</p>
      </div>

      {/* Filtros compactos */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={periodo} onValueChange={setPeriodo}>
          <SelectTrigger className="h-9 w-[170px]" data-testid="iv-periodo"><SelectValue /></SelectTrigger>
          <SelectContent>{PERIODOS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
        </Select>

        {periodo === "custom" && (
          <div className="flex items-center gap-1.5 rounded-md border px-2 py-1">
            <Input type="date" value={custom.desde} max={custom.hasta} onChange={(e) => setCustom((c) => ({ ...c, desde: e.target.value }))} className="h-7 w-[140px] border-0 p-0 shadow-none focus-visible:ring-0" data-testid="iv-desde" />
            <span className="text-xs text-muted-foreground">–</span>
            <Input type="date" value={custom.hasta} min={custom.desde} onChange={(e) => setCustom((c) => ({ ...c, hasta: e.target.value }))} className="h-7 w-[140px] border-0 p-0 shadow-none focus-visible:ring-0" data-testid="iv-hasta" />
          </div>
        )}

        <EstadosPopover estados={estados} setEstados={setEstados} />
        <SourcesPopover sources={sources} setSources={setSources} disponibles={sourcesDisponibles} />

        <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => q.refetch()} disabled={q.isFetching} data-testid="iv-refresh">
          {q.isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>

      {q.isLoading ? (
        <Skeleton className="h-72 w-full" />
      ) : (
        <>
          {/* KPIs del total (agregado) */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi icon={TrendingUp} label="Ventas (equipo)" value={total.ventas ?? 0} testid="iv-kpi-ventas" />
            <Kpi icon={Users} label="Leads (equipo)" value={total.leads ?? 0} testid="iv-kpi-leads" />
            <Kpi icon={Percent} label="Conversión media" value={pct(total.conversion_pct)} testid="iv-kpi-conv" />
            <Kpi icon={Coins} label="Rentabilidad media" value={pct(total.rentabilidad_pct)} testid="iv-kpi-rent" />
          </div>

          {/* Tabla por FUENTE (origen del lead) */}
          <Card>
            <CardContent className="p-0">
              <div className="border-b bg-muted/30 px-4 py-2.5">
                <div className="flex items-center gap-2 font-semibold">
                  <Compass className="h-4 w-4 text-primary" />
                  Rentabilidad por fuente
                </div>
                <p className="text-[11px] text-muted-foreground">Segmentación por canal de captación (KimKim, Zicasso, Direct...)</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="iv-table-sources">
                  <thead>
                    <tr className="border-b bg-muted/20 text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="sticky left-0 z-10 bg-muted/20 px-4 py-3 font-semibold">Fuente</th>
                      {SOURCE_COLS.map((c) => <th key={c.key} className="px-4 py-3 text-right font-semibold">{c.label}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {bySource.length === 0 ? (
                      <tr><td colSpan={SOURCE_COLS.length + 1} className="px-4 py-6 text-center text-muted-foreground">Sin datos.</td></tr>
                    ) : bySource.map((r) => (
                      <tr key={r.fuente} className="border-b transition-colors hover:bg-muted/40" data-testid={`iv-source-row-${r.fuente}`}>
                        <td className="sticky left-0 z-10 bg-background px-4 py-3 font-medium capitalize">
                          {r.fuente === "sin_fuente" ? "(sin fuente)" : r.fuente}
                        </td>
                        {SOURCE_COLS.map((c) => <td key={c.key} className="px-4 py-3 text-right tabular-nums">{c.fmt(r[c.key], r)}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Tabla por agente + total */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="iv-table">
                  <thead>
                    <tr className="border-b bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="sticky left-0 z-10 bg-muted/40 px-4 py-3 font-semibold">Agente</th>
                      {COLS.map((c) => <th key={c.key} className="px-4 py-3 text-right font-semibold">{c.label}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr><td colSpan={COLS.length + 1} className="px-4 py-10 text-center text-muted-foreground">Sin datos en este periodo.</td></tr>
                    ) : rows.map((r) => (
                      <tr key={r.agente} className="border-b transition-colors hover:bg-muted/40" data-testid={`iv-row-${r.agente}`}>
                        <td className="sticky left-0 z-10 bg-background px-4 py-3 font-medium">{r.agente}</td>
                        {COLS.map((c) => <td key={c.key} className="px-4 py-3 text-right tabular-nums">{c.fmt(r[c.key], r)}</td>)}
                      </tr>
                    ))}
                  </tbody>
                  {rows.length > 0 && (
                    <tfoot>
                      <tr className="border-t-2 bg-primary/5 font-semibold" data-testid="iv-row-total">
                        <td className="sticky left-0 z-10 bg-primary/5 px-4 py-3">{total.agente || "Total (agregado)"}</td>
                        {COLS.map((c) => <td key={c.key} className="px-4 py-3 text-right tabular-nums">{c.fmt(total[c.key], total)}</td>)}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Nota de fórmulas */}
          <p className="flex items-start gap-2 text-xs text-muted-foreground" data-testid="iv-formulas">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              <strong>% Conversión</strong> = ventas ÷ leads (máx. 100 %). &nbsp;
              <strong>Margen medio venta</strong> = (ingresado − cotizado) ÷ precio de venta (foto en el momento de la venta, cualquier estado seleccionado). &nbsp;
              <strong>Rentabilidad media</strong> = (ingresado − coste real) ÷ precio de venta (post-viaje). Solo se calcula sobre viajes en estado <strong>Cerrado</strong> o <strong>Terminado</strong>, cuando el coste real de proveedores ya es fiable.
            </span>
          </p>
        </>
      )}
    </div>
  );
}
